/**
 * Name suggestions for the Medications tab's add/edit form, from two free,
 * keyless U.S. government lookup services (the same ones the patient portal
 * uses, healthOS/Patient-portal/src/hooks/useRegimenSuggestions.ts):
 *
 *   - medications → NLM Clinical Tables RxTerms (prescriber-friendly names,
 *     each with its marketed strengths and the RxNorm code of each strength);
 *   - supplements → the NIH Dietary Supplement Label Database (RxNorm barely
 *     covers supplements).
 *
 * They suggest names only; they hold no patient data and nothing patient-
 * specific is sent to them, only the letters typed. NLM's terms require the
 * attribution line the form shows (NLM_ATTRIBUTION).
 *
 * Pure parsing lives here, apart from the fetching hook, so tests can pin the
 * response shapes without mounting the app.
 */

/** Peptides suggest from RxTerms, like medications; supplements from the supplement label database. */
export type MedicationKind = 'medication' | 'supplement' | 'peptide';

export interface SuggestedStrength {
  /** e.g. "500 mg Tab" */
  text: string;
  /** RxNorm code for this drug at this strength; '' when the service sent none. */
  rxcui: string;
}

export interface MedicationSuggestion {
  label: string;
  /** Marketed strengths (medications only). */
  strengths: SuggestedStrength[];
}

export const MAX_SUGGESTIONS = 7;

export const NLM_ATTRIBUTION =
  'Suggestions use publicly available data from the U.S. National Library of Medicine (NLM), ' +
  'National Institutes of Health, Department of Health and Human Services; NLM is not responsible ' +
  'for this product and does not endorse or recommend this or any other product.';

export function rxTermsUrl(term: string): string {
  return (
    'https://clinicaltables.nlm.nih.gov/api/rxterms/v3/search' +
    `?terms=${encodeURIComponent(term)}&ef=STRENGTHS_AND_FORMS,RXCUIS&maxList=${MAX_SUGGESTIONS}`
  );
}

export function dsldUrl(term: string): string {
  return `https://api.ods.od.nih.gov/dsld/v9/search-filter?q=${encodeURIComponent(term)}&size=20`;
}

/**
 * RxTerms answers `[total, names[], { STRENGTHS_AND_FORMS, RXCUIS }, display]`,
 * where the two extra-field lists run parallel to `names` and to each other.
 * Anything else yields no suggestions.
 */
export function parseRxTerms(data: unknown): MedicationSuggestion[] {
  if (!Array.isArray(data) || !Array.isArray(data[1])) return [];
  const names = data[1] as unknown[];
  const extra = (data[2] && typeof data[2] === 'object' ? data[2] : {}) as {
    STRENGTHS_AND_FORMS?: unknown;
    RXCUIS?: unknown;
  };
  const strengthLists = Array.isArray(extra.STRENGTHS_AND_FORMS) ? extra.STRENGTHS_AND_FORMS : [];
  const codeLists = Array.isArray(extra.RXCUIS) ? extra.RXCUIS : [];

  const out: MedicationSuggestion[] = [];
  names.slice(0, MAX_SUGGESTIONS).forEach((name, i) => {
    if (typeof name !== 'string' || name.trim() === '') return;
    const strengths = Array.isArray(strengthLists[i]) ? (strengthLists[i] as unknown[]) : [];
    const codes = Array.isArray(codeLists[i]) ? (codeLists[i] as unknown[]) : [];
    out.push({
      label: name.trim(),
      strengths: strengths
        .map((text, j) => ({
          text: typeof text === 'string' ? text.trim() : '',
          rxcui: typeof codes[j] === 'string' && /^\d+$/.test(codes[j] as string) ? (codes[j] as string) : '',
        }))
        .filter((s) => s.text !== ''),
    });
  });
  return out;
}

/** DSLD answers `{ hits: [{ _source: { fullName, offMarket } }] }`; off-market and repeat labels are dropped. */
export function parseDsld(data: unknown): MedicationSuggestion[] {
  const hits = (data as { hits?: unknown } | null)?.hits;
  if (!Array.isArray(hits)) return [];

  const seen = new Set<string>();
  const out: MedicationSuggestion[] = [];
  for (const hit of hits) {
    const source = (hit as { _source?: { fullName?: unknown; offMarket?: unknown } } | null)?._source;
    const name = typeof source?.fullName === 'string' ? source.fullName.trim() : '';
    if (!name || source?.offMarket) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ label: name, strengths: [] });
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
}
